/**
 * Relay client — maintains a long-lived WebSocket to Hub's `/relay/<workspace>`
 * endpoint and fans inbound events out to subscribers. Used for live
 * message sync + reaction events + presence deltas, without having to
 * re-poll the REST API.
 *
 * Shared between Web and Desktop renderer — the browser's native WebSocket
 * works in both. Clerk JWT is injected via the same getToken() callback
 * the HubClient uses.
 *
 * Ported from kritaka-electron/src/main/services/relay-client.ts with two
 * adaptations: (1) uses the browser WebSocket (ArrayBuffer/Blob instead of
 * `ws` library), (2) no `ws.ping()` pong path — browser WS doesn't expose
 * the ping frame API, so zombie detection relies on app-level heartbeats.
 */
// Bound the pre-connect queue so a wedged socket can't grow unbounded.
// Sending more than this at start-up would be pathological; oldest frames
// are dropped first (FIFO would preserve newer intent over older).
const MAX_PENDING_SENDS = 100;
export function createRelayClient(options) {
    const reconnectDelay = options.reconnectDelayMs ?? 5000;
    const clientType = options.clientType ?? "web";
    const handlers = new Set();
    const connListeners = new Set();
    let ws = null;
    let reconnectTimer = null;
    let currentWorkspaceId = null;
    let disposed = false;
    const pendingSends = [];
    function setConnectionState(state) {
        for (const cb of connListeners)
            cb(state);
    }
    function scheduleReconnect() {
        if (disposed || reconnectTimer)
            return;
        if (!currentWorkspaceId)
            return;
        reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            if (currentWorkspaceId)
                void doConnect(currentWorkspaceId);
        }, reconnectDelay);
    }
    async function doConnect(workspaceId) {
        if (disposed)
            return;
        currentWorkspaceId = workspaceId;
        setConnectionState("connecting");
        // Tear down old socket if any.
        if (ws) {
            const old = ws;
            ws = null;
            try {
                old.close();
            }
            catch {
                /* ignore */
            }
        }
        const token = await options.getToken();
        if (!token) {
            console.warn("[relay-client] no auth token, retrying");
            scheduleReconnect();
            return;
        }
        // Browser WS can't set Authorization header — pass the token as a
        // query param. Hub must accept either.
        const base = options.baseUrl.replace(/^https?:\/\//, "wss://").replace(/^wss:\/\/wss:\/\//, "wss://");
        const url = `${base.replace(/\/$/, "")}/relay/${workspaceId}?clientType=${encodeURIComponent(clientType)}&token=${encodeURIComponent(token)}`;
        const sock = new WebSocket(url);
        ws = sock;
        sock.onopen = () => {
            if (ws !== sock)
                return;
            setConnectionState("connected");
            // Drain anything that accumulated while we were pre-OPEN. Browser
            // WebSocket.send throws if the socket goes away mid-flush, so we copy
            // the queue first and bail on the first failure; anything left
            // gets retried on the next reconnect.
            if (pendingSends.length > 0) {
                const drain = pendingSends.splice(0, pendingSends.length);
                for (const raw of drain) {
                    try {
                        sock.send(raw);
                    }
                    catch {
                        // Socket died mid-flush — requeue and stop.
                        pendingSends.unshift(raw);
                        break;
                    }
                }
            }
        };
        sock.onmessage = (ev) => {
            if (ws !== sock)
                return;
            const raw = typeof ev.data === "string" ? ev.data : null;
            if (!raw)
                return;
            let msg;
            try {
                msg = JSON.parse(raw);
            }
            catch {
                return;
            }
            for (const cb of handlers) {
                try {
                    cb(msg);
                }
                catch (err) {
                    console.warn("[relay-client] handler threw", err);
                }
            }
        };
        sock.onerror = () => {
            if (ws !== sock)
                return;
            console.warn("[relay-client] socket error");
        };
        sock.onclose = () => {
            if (ws !== sock)
                return;
            ws = null;
            setConnectionState("disconnected");
            scheduleReconnect();
        };
    }
    return {
        connect(workspaceId) {
            disposed = false;
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            void doConnect(workspaceId);
        },
        disconnect() {
            disposed = true;
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            if (ws) {
                const old = ws;
                ws = null;
                try {
                    old.close();
                }
                catch {
                    /* ignore */
                }
            }
            currentWorkspaceId = null;
            // Explicit disconnect — drop anything queued. Transient reconnects
            // keep the queue so pre-OPEN intent survives a brief outage.
            pendingSends.length = 0;
            setConnectionState("disconnected");
        },
        onMessage(handler) {
            handlers.add(handler);
            return () => {
                handlers.delete(handler);
            };
        },
        onConnectionState(listener) {
            connListeners.add(listener);
            return () => {
                connListeners.delete(listener);
            };
        },
        send(frame) {
            let raw;
            try {
                raw = JSON.stringify(frame);
            }
            catch {
                return false;
            }
            if (ws && ws.readyState === 1 /* OPEN */) {
                try {
                    ws.send(raw);
                    return true;
                }
                catch {
                    // Fall through to queue — if ws.send throws, the socket is
                    // tearing down and the frame is better off replayed on reconnect.
                }
            }
            if (pendingSends.length >= MAX_PENDING_SENDS) {
                pendingSends.shift();
            }
            pendingSends.push(raw);
            return true;
        },
        isConnected() {
            return ws !== null && ws.readyState === 1;
        },
    };
}
//# sourceMappingURL=relay-client.js.map